import classNames from "classnames";
import { FC, ReactElement, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { usePageviewTracker } from "./analytics/SiteImprove";
import { useSiteImprove } from "./analytics/useSiteImprove";
import styles from "./App.module.css";
import Header from "./header/Header";
import { linkExist } from "./infrastructure/linkUtils";
import { RedirectToCorrectForm } from "./infrastructure/redirect/RedirectToCorrectForm";
import type { Dispatch } from "./infrastructure/redux/actions";
import {
  fetchEntrypointAction,
  setControlFlow,
} from "./infrastructure/redux/app/app-actions";
import type { ReduxState } from "./infrastructure/redux/state";
import { useAsyncError } from "./infrastructure/useAsyncError";
import { useRootPath } from "./infrastructure/useRootPath";
import { UnderkontoSkjema } from "./manuell/UnderkontoSkjema";
import { CompleteOAuth } from "./person/complete-oauth/CompleteOAuth";
import { NotRegistered } from "./person/not-registered/NotRegistered";
import { Person } from "./person/Person";
import { PersonForm } from "./person/PersonForm";
import { PersonGjenapne } from "./person/PersonGjenapne";
import StatusPageBanner from "./statusbanner/StatusPageBanner";

interface RequireAuthProps {
  children: ReactElement;
  acceptedAuth: string;
  redirectTo: string;
}

const RequireAuth: FC<RequireAuthProps> = ({
  children,
  acceptedAuth,
  redirectTo,
}) => {
  const currentAuth = useSelector((state: ReduxState) => state.user.auth);

  const isAuthenticated = currentAuth === acceptedAuth;

  return isAuthenticated ? children : <Navigate to={redirectTo} />;
};

const App: FC = () => {
  const loadingDone = useSelector((state: ReduxState) =>
    linkExist("session", state.link),
  );

  useSiteImprove(true);

  const rootPath = useRootPath();

  const location = useLocation();

  const dispatch = useDispatch<Dispatch>();

  const controlFlowCalled = useRef(false);

  useEffect(() => {
    if (!controlFlowCalled.current) {
      dispatch(setControlFlow());
      controlFlowCalled.current = true;
    }
  }, [dispatch]);

  const throwError = useAsyncError();

  useEffect(() => {
    const fetchEntrypoint = async () => {
      try {
        await dispatch(fetchEntrypointAction());
      } catch (error) {
        throwError(error);
      }
    };

    fetchEntrypoint();
  }, [dispatch]);

  usePageviewTracker();

  if (!loadingDone) {
    return null;
  }

  const personIntroUrl = `${rootPath}/person`;

  return (
    <>
      <StatusPageBanner />
      <Header />
      <article
        className={classNames(
          styles.appArticle,
          location.pathname.endsWith("/sconfirm") &&
            styles.wideSigneringConfirmArticle,
        )}
      >
        <Routes>
          <Route path={"registrering/:language?"}>
            <Route path="underkonto">
              <Route
                path=":accountId"
                element={
                  <RequireAuth
                    acceptedAuth={"CORPORATION"}
                    redirectTo={`${rootPath}/underkonto/feil`}
                  >
                    <UnderkontoSkjema />
                  </RequireAuth>
                }
              />
            </Route>
            <Route path="person">
              <Route index={true} element={<Person />} />
              <Route path="not-registered" element={<NotRegistered />} />
              <Route
                path="form"
                element={
                  <RequireAuth
                    acceptedAuth={"REGISTRATION"}
                    redirectTo={personIntroUrl}
                  >
                    <PersonForm />
                  </RequireAuth>
                }
              />
              <Route
                path="gjenapne"
                element={
                  <RequireAuth
                    acceptedAuth={"REGISTRATION"}
                    redirectTo={personIntroUrl}
                  >
                    <PersonGjenapne />
                  </RequireAuth>
                }
              />
              <Route path="complete-oauth" element={<CompleteOAuth />} />
            </Route>
            <Route index={true} element={<RedirectToCorrectForm />} />
          </Route>
        </Routes>
      </article>
    </>
  );
};

export default App;
